import { formatForCopy, tokenize } from '@engpod/core';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { copyText } from '../lib/format';
import type { DictEntry, EpisodeSummary, SegmentView, VocabItem } from '../types';
import { Sentence } from './Sentence';

interface Props {
  segment: SegmentView;
  tokenIndex: number;
  episode: EpisodeSummary;
  programTitle?: string;
  onPlay(): void;
  onClose(): void;
  onSaved?(item: VocabItem): void;
}

/**
 * Tap a word → dictionary + its sentence. Tap another word in the sentence to extend
 * the selection into a phrase. Saving always stores the sentence with it.
 */
export function WordSheet({ segment, tokenIndex, episode, programTitle, onPlay, onClose, onSaved }: Props) {
  const { me } = useAuth();
  const tokens = useMemo(() => tokenize(segment.en), [segment.en]);
  const [range, setRange] = useState<[number, number]>([tokenIndex, tokenIndex]);
  const [entry, setEntry] = useState<DictEntry | null>(null);
  const [meaning, setMeaning] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setRange([tokenIndex, tokenIndex]), [tokenIndex, segment.id]);
  const isPhrase = range[0] !== range[1];
  const phrase = tokens.slice(range[0], range[1] + 1).map((t) => t.text).join('').trim();
  const firstWord = tokens[range[0]]?.word ?? '';

  useEffect(() => {
    setSaved(false);
    setStatus(null);
    let cancelled = false;
    const term = isPhrase ? phrase.toLowerCase() : firstWord;
    if (!term) return;
    api<DictEntry>(`/dict/${encodeURIComponent(term)}`)
      .then((d) => {
        if (cancelled) return;
        setEntry(d);
        setMeaning(d.zh?.split('\n')[0]?.slice(0, 120) ?? '');
      })
      .catch(() => !cancelled && setEntry(null));
    return () => {
      cancelled = true;
    };
  }, [isPhrase, phrase, firstWord]);

  const extend = (i: number) => {
    if (!tokens[i]?.word) return;
    setRange(([a, b]) => (i < a ? [i, b] : i > b ? [a, i] : [i, i]));
  };

  const surface = isPhrase ? phrase : (entry?.found ? entry.lemma : firstWord);
  const save = async () => {
    setStatus('保存中…');
    try {
      const { item } = await api<{ item: VocabItem }>('/vocab', {
        method: 'POST',
        json: { kind: isPhrase ? 'expression' : 'word', surface, termKey: surface, meaning: meaning || undefined, note: note || undefined, segmentId: segment.id },
      });
      setSaved(true);
      setStatus(item.contexts.length > 1 ? `已加入生词本（这个词现在有 ${item.contexts.length} 个例句）` : '已加入生词本');
      onSaved?.(item);
      window.dispatchEvent(new Event('engpod:review-changed'));
    } catch (e) {
      setStatus(errorText(e));
    }
  };

  const copy = async () => {
    await copyText(formatForCopy({ term: surface, meaning, en: segment.en, zh: segment.zh, program: programTitle, episodeTitle: episode.title, date: episode.publishedOn ?? undefined }));
    setStatus('已复制（含原句和出处）');
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <section className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="查词">
        <header className="row spread">
          <div>
            <h3 className="term">{surface}</h3>
            {!isPhrase && entry?.ipa && <small className="muted">/{entry.ipa}/</small>}
          </div>
          <button className="plain" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </header>
        {!isPhrase && entry?.found && <p className="dict">{entry.zh}</p>}
        {!isPhrase && entry && !entry.found && <p className="muted">词典里没有这个词，可以自己写释义。</p>}
        <div className="context-box">
          <Sentence text={segment.en} onWord={extend} highlight={range} />
          {segment.zh && <div className="zh">{segment.zh}</div>}
          <small className="muted">点句子里的其他词可扩展成词组</small>
        </div>
        <label className="field">
          <span>释义</span>
          <input value={meaning} onChange={(e) => setMeaning(e.target.value)} placeholder={isPhrase ? '这个表达在本句中的意思' : ''} />
        </label>
        <label className="field">
          <span>备注</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="可选：自己的理解、联想" />
        </label>
        <div className="row wrap">
          {me?.user ? (
            <button className="primary" onClick={save} disabled={saved}>
              {saved ? '已加入' : isPhrase ? '加入表达本' : '加入生词本'}
            </button>
          ) : (
            <Link className="button primary" to="/me">
              登录后保存
            </Link>
          )}
          <button onClick={onPlay}>▶ 播放这句</button>
          <button onClick={copy}>复制</button>
        </div>
        {status && <p className="status">{status}</p>}
      </section>
    </div>
  );
}
